import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { SEOHead } from "@/components/seo/seo-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { showToast } from "@/lib/toast";
import { Wallet, Shield, UserPlus, Eye, EyeOff } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";

export function RegisterPage() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("auth.passwordsNotMatch"));
      return;
    }

    if (password.length < 8) {
      setError(t("auth.passwordTooShort"));
      return;
    }

    if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError("Password must contain at least one letter and one number");
      return;
    }

    setIsLoading(true);

    try {
      await register(username, email, password);
      showToast.success(t("auth.accountCreated"));
      navigate("/");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("auth.registrationFailed");
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Register - Personal Finance App"
        description="Create a free account to start managing your personal finances. Track income, expenses, assets, and achieve your financial goals."
        keywords="register, sign up, create account, personal finance, free account"
        canonicalUrl="/register"
      />
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 relative overflow-hidden z-0">
      {/* Decorative gradient orbs - subtle, hidden on very small screens */}
      <div className="hidden sm:block absolute top-0 left-0 w-[300px] sm:w-[400px] md:w-[500px] h-[300px] sm:h-[400px] md:h-[500px] bg-chart-1/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="hidden sm:block absolute bottom-0 right-0 w-[300px] sm:w-[400px] md:w-[500px] h-[300px] sm:h-[400px] md:h-[500px] bg-chart-2/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-6 sm:gap-8 items-center relative z-10">
        {/* Left side - Branding */}
        <div className="hidden md:block space-y-6 animate-in fade-in slide-in-from-left duration-700">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-4xl font-bold">Personal Finance</h1>
            </div>
            <p className="text-xl text-muted-foreground">
              {t("auth.registerToStart")}
            </p>
          </div>
          
          <div className="space-y-4 pt-8">
<div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <UserPlus className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">{t("auth.freeAccount")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("auth.freeAccountDescription")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">{t("auth.dataSecure")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("auth.dataSecureDescription")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Register Card */}
        <div className="animate-in fade-in slide-in-from-right duration-700">
          <Card className="w-full shadow-xl shadow-black/15 dark:shadow-black/50 border">
            <CardHeader className="text-center space-y-4 pb-6">
              <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                <Wallet className="h-8 w-8 text-primary" />
              </div>
              <div className="md:hidden">
                <CardTitle className="text-3xl font-bold">Personal Finance</CardTitle>
              </div>
              <CardTitle className="text-2xl font-bold hidden md:block">{t("auth.createAccount")}</CardTitle>
              <CardDescription className="text-base">
                {t("auth.registerToStart")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="username">{t("settings.username")}</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="johndoe"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("settings.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm p-1"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                      ) : (
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                      )}
                    </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Min. 8 characters with at least one letter and one number
                    </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm p-1"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                      ) : (
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200" 
                  disabled={isLoading}
                  size="lg"
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  {isLoading ? t("auth.creatingAccount") : t("auth.createAccount")}
                </Button>
              </form>
              
              <Separator />
              
              <div className="text-center text-sm">
                <span className="text-muted-foreground">{t("auth.alreadyHaveAccount")} </span>
                <Link to="/login" className="text-primary hover:underline font-medium">
                  {t("auth.login")}
                </Link>
              </div>
              
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Shield className="h-3 w-3" />
                <span>{t("auth.dataSecure")}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </>
  );
}

